import {
  IonIcon,
  IonModal,
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import {
  alertCircleOutline,
  banOutline,
  calendarClearOutline,
  calendarOutline,
  callOutline,
  checkmark,
  checkmarkDoneOutline,
  chevronDown,
  close,
  createOutline,
  documentTextOutline,
  ellipsisHorizontal,
  exitOutline,
  hourglassOutline,
  mailOutline,
  notificationsOutline,
  peopleOutline,
  personAddOutline,
  playOutline,
  remove,
  shieldCheckmarkOutline,
  timeOutline,
} from "ionicons/icons";
import dayjs from "dayjs";
import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Redirect, useParams } from "react-router-dom";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../navigation/studyTabs";
import {
  deleteSuiviGroupMember,
  deleteSuiviMemberDeath,
  deleteSuiviRoleAbsence,
  ExitReasonId,
  getSuiviGroupDetail,
  postSuiviGroupExit,
  postSuiviMemberAccessLink,
  postSuiviMemberConsents,
  postSuiviMemberDeath,
  postSuiviMemberExit,
  postSuiviReminderStamp,
  postSuiviRoleAbsence,
  SuiviConsentGate,
  SuiviConsentsBody,
  SuiviConsentSource,
  SuiviConsentState,
  SuiviConsentStatus,
  SuiviExitBody,
  SuiviFillSequence,
  SuiviGroupDetail,
  SuiviMemberConsent,
  SuiviMemberDetail,
  SuiviReferenceDateStatus,
  SuiviRoleAbsence,
  SuiviSequenceBase,
} from "../../services/suivi.service";
import { getWelcomeInfo } from "../../services/weclome.service";
import { canManageDossiers } from "../../helper/familyComposition";
import { AddMemberModal } from "./DossierModals";
import { AbsenceConfirmModal, ConsentSheet } from "./ConsentModals";
import { DeathModal, ExitModal, MemberActionsModal } from "./ExitModals";
import { AccessLinkModal } from "../../components/organisms/Invitation/InviteModals";
import useToast from "../../hooks/useToast";
import {
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
} from "../../components/atoms/ModalKit";
import { RoleEnum } from "../../types/invitation.types";
import CallSheet from "./CallSheet";
import {
  CardAction,
  CardFooter,
  Column,
  Columns,
  DueChip,
  EmptyState,
  FillPill,
  FoldGlyph,
  FooterLine,
  IconButton,
  IconTile,
  insetSeparator,
  ListCard,
  MemberInitial,
  MetaSep,
  OutlinePill,
  Page,
  QuietTag,
  Row,
  RowEnd,
  RowMain,
  RowSub,
  RowTitle,
  SectionHeader,
  StatusMark,
  StatusText,
  SUIVI_CANVAS,
  SuiviTone,
  memberRoleOrdinalLabel,
} from "./suiviUi";

/**
 * Espace groupe (famille) — everything about one dossier. Layout contract:
 * Membres (call pills on members whose role collects a phone) →
 * « À remplir par vous » (warm CTA, straight into the runner, returns HERE) →
 * « Côté participants » (read-only status + relance tag + call) →
 * « Historique » folded (done ✓ / missed, never actionable).
 * Wide screens split this into two stacks — the dossier (members, dates,
 * consents) beside the work — without reordering anything on a phone.
 */
/* Tight stack for the future-window sentences (Page's 14px gap is too airy
   for what should read as one quiet block). */
const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 6px;
`;

// Reference-date rule status → the round mark leading its row.
const REFDATE_MARK: Record<SuiviReferenceDateStatus["status"], { tone: SuiviTone; icon: string }> = {
  RESOLVED: { tone: "success", icon: checkmark },
  WAITING: { tone: "info", icon: hourglassOutline },
  NO_SOURCE: { tone: "neutral", icon: remove },
  UNCONFIGURED: { tone: "neutral", icon: alertCircleOutline },
};

// A consent state reads by its tone first: granted / refused / still to collect.
const consentTone = (status: SuiviConsentStatus): SuiviTone =>
  status === "GRANTED" ? "success" : status === "REFUSED" ? "danger" : "info";
const consentIcon = (status: SuiviConsentStatus): string =>
  status === "GRANTED" ? checkmark : status === "REFUSED" ? close : hourglassOutline;

const GroupSpace: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { groupKey } = useParams<{ groupKey: string }>();
  // Pushed URLs carry the key percent-encoded (external_id is free text);
  // react-router v5 hands the segment back raw, so decode here. SAFE decode:
  // a mangled deep link (dangling % escape) must fall through to the API's
  // uniform 404, not throw URIError during render.
  const rawGroupKey = useMemo(() => {
    try {
      return decodeURIComponent(groupKey);
    } catch {
      return groupKey;
    }
  }, [groupKey]);
  const { study, selectedRole } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [detail, setDetail] = useState<SuiviGroupDetail | null>(null);
  const [isError, setIsError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [callMember, setCallMember] = useState<SuiviMemberDetail | null>(null);
  // Dossier management (médecin authoritative: add always, remove only data-empty).
  const [canManage, setCanManage] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<SuiviMemberDetail | null>(null);
  const [removing, setRemoving] = useState(false);
  // Exit / décès: one chooser per member, then the matching form. exitTarget === null
  // with exitDossier true means "the whole dossier".
  const [actionsMember, setActionsMember] = useState<SuiviMemberDetail | null>(null);
  const [exitTarget, setExitTarget] = useState<SuiviMemberDetail | null>(null);
  const [exitDossier, setExitDossier] = useState(false);
  const [deathTarget, setDeathTarget] = useState<SuiviMemberDetail | null>(null);
  const [accessLinkTarget, setAccessLinkTarget] = useState<SuiviMemberDetail | null>(null);
  // Consents recorded outside the app (fiche papier / phone) and dossier absence
  // declarations — both reached from the « Consentements » card / member ⋯ menu.
  const [consentSheetMember, setConsentSheetMember] = useState<SuiviMemberDetail | null>(null);
  const [absenceTarget, setAbsenceTarget] = useState<SuiviRoleAbsence | null>(null);
  // Reason the ExitModal opens on. EVERY opener sets it explicitly (the modal's own
  // onDismiss never runs finishAction), so the opposition shortcut's preselection can
  // never leak into the next unrelated exit.
  const [exitDefaultReason, setExitDefaultReason] = useState<ExitReasonId>(
    ExitReasonId.WITHDRAWAL
  );
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const present = useToast();

  // True only while this view is the active Ionic page — a late fetch response
  // must never rewrite the GLOBAL layout title of whatever page is now shown.
  const isActiveRef = useRef(false);
  useIonViewWillLeave(() => {
    isActiveRef.current = false;
  });

  const fetchDetail = useCallback((): Promise<void> => {
    if (!study?.id) return Promise.resolve();
    // Never fire the investigator-only request while the self-guard below is
    // redirecting a known non-investigator away.
    if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
      return Promise.resolve();
    }
    return getSuiviGroupDetail({ studyId: study.id, groupKey: rawGroupKey, lang: language })
      .then(({ data }) => {
        setIsError(false);
        setDetail(data);
        if (isActiveRef.current) {
          setResonanceLayout({
            title: data.external_id
              ? t("suivi_dossier", { code: data.external_id })
              : t("suivi_group_title"),
          });
        }
      })
      .catch(() => setIsError(true));
    // setResonanceLayout/t are stable enough for this page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id, rawGroupKey, language, selectedRole]);

  // Refetch on every entry — including the return from the questionnaire runner,
  // so a just-filled row moves to Historique immediately (trampoline pattern:
  // useIonViewWillEnter registers once).
  const willEnterRef = useRef<(() => void) | undefined>(undefined);
  willEnterRef.current = () => {
    isActiveRef.current = true;
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: SUIVI_CANVAS,
      // Re-entering with data already loaded keeps the dossier title stable.
      title: detail?.external_id
        ? t("suivi_dossier", { code: detail.external_id })
        : "suivi_group_title",
      welcomePage: false,
      headerLeft: ["back"],
      headerBackFallbackPath: `/${study_slug}/tabs/suivi`,
    });
    fetchDetail();
  };
  useIonViewWillEnter(() => {
    willEnterRef.current?.();
  });

  // In-place language change (hamburger menu) never re-enters the view — refetch
  // so API-translated labels follow the UI language (fetchDetail deps include it).
  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Same capability gate as SuiviHome: dossier management only where families
  // exist (≥1 data-only subject role in the cached welcome catalog). Fail-soft.
  useEffect(() => {
    let cancelled = false;
    if (!study_slug || !selectedRole) return undefined;
    getWelcomeInfo({ slug: study_slug, lang: language })
      .then(({ data: welcome }) => {
        if (!cancelled) setCanManage(canManageDossiers(selectedRole, welcome.roles));
      })
      .catch(() => {
        /* affordances simply stay hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [study_slug, language, selectedRole]);

  if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
    return <Redirect to={getDefaultTabPath(study_slug, selectedRole.name)} />;
  }

  const memberById = new Map(
    (detail?.members ?? []).map((member) => [member.user_id, member])
  );

  // Role label with sibling ordinal (« Enfant 2 ») — the only way to tell
  // same-role members apart before a fiche names them.
  const memberRoleLabel = (member: SuiviMemberDetail): string =>
    memberRoleOrdinalLabel(member, detail?.members ?? [], t("suivi_member_generic"));

  const memberDisplayName = (member: SuiviMemberDetail): string => {
    const name = [member.firstname, member.lastname].filter(Boolean).join(" ");
    // Identity fallback chain: ordinal role label leads, name is garnish once
    // an identity question collects it (« Enfant 2 — Léa »).
    const role = memberRoleLabel(member);
    return name ? `${role} — ${name}` : role;
  };

  // Sequence rows carry subject_user_id — label them with the live member
  // (ordinal + name) so « À remplir » rows say WHICH child they are about.
  // Fallback: role label from the row (member gone, e.g. history of a removed one).
  const labelForSubject = (
    subjectUserId: number,
    fallbackRoleLabel: string | null
  ): string | null => {
    const member = memberById.get(subjectUserId);
    return member ? memberDisplayName(member) : fallbackRoleLabel;
  };

  // Dossier reference-date rules — case mode only (an older API sends none). The
  // « Dates de référence » card and the per-member « Date … manquante » hint both
  // read from this one list, so they can never disagree.
  const caseReferenceDates: SuiviReferenceDateStatus[] =
    detail?.grouping_mode === "case" ? (detail.reference_dates ?? []) : [];

  // Rules still missing THIS member's own date (their fiche is not done yet). Keyed on the
  // data, not on the status: a rule that does not wait for all (NO_SOURCE, or already
  // RESOLVED on a sibling) still names the members who owe their date.
  const missingReferenceDates = (member: SuiviMemberDetail): SuiviReferenceDateStatus[] =>
    caseReferenceDates.filter((refDate) =>
      refDate.missing_visible_user_ids.includes(member.user_id)
    );

  // « Manque : Enfant 2 · 1 hors de votre site » — members of this site by name,
  // the others only as a count (the visibility gate is the server's, we never
  // learn who they are). Null when nothing is nameable.
  const missingReferenceDateWho = (refDate: SuiviReferenceDateStatus): string | null => {
    const visible = refDate.missing_visible_user_ids
      .map((userId) => labelForSubject(userId, null))
      .filter(Boolean)
      .join(", ");
    const hidden =
      refDate.missing_hidden_count > 0
        ? t("suivi_refdate_missing_hidden", { n: refDate.missing_hidden_count })
        : "";
    const who = [visible, hidden].filter(Boolean).join(" · ");
    return who || null;
  };

  // « Consentement à recueillir » — an option applicable to this member with nothing
  // recorded yet. Never on someone who left or died: there is nobody left to ask.
  const consentToCollect = (member: SuiviMemberDetail): boolean =>
    !member.exited &&
    !member.deceased &&
    (member.consents ?? []).some((consent) => consent.status === null);

  // Exactly what the card body can render, and nothing more: a member carrying at least
  // one option, a gate row that is not NOT_APPLICABLE, or an absence row the body keeps
  // (already declared, or declarable while this user may manage the dossier). Mirroring
  // the three predicates below is what stops an empty « Consentements » heading — e.g. a
  // study whose only consent content is an undeclared absence, read by someone without
  // dossier rights. Every field is optional, so an older API or a study without options
  // changes nothing here.
  // `declarable` is a ROLE CONFIG fact (the role carries absence_declarable_by); `has_member`
  // is the dossier fact — a member the GATE waits on, the very predicate the write enforces
  // (409 ROLE_HAS_MEMBER). Never gate this on mere presence: a credential-less placeholder
  // neither satisfies nor waives the role, so hiding the action there would leave the child's
  // questionnaires waiting with no way out. The row is TITLED as the question it asks
  // (« Aucun(e) Père dans ce dossier ? ») so it never reads as a second member row.
  const canDeclareAbsent = (absence: SuiviRoleAbsence): boolean =>
    absence.declarable && canManage && !absence.has_member;

  const hasConsentSection =
    (detail?.members ?? []).some((member) => (member.consents?.length ?? 0) > 0) ||
    (detail?.consent_gates ?? []).some((gate) => gate.state !== "NOT_APPLICABLE") ||
    (detail?.role_absences ?? []).some(
      (absence) => absence.is_absent || canDeclareAbsent(absence)
    );

  const consentStatusWord = (status: SuiviConsentStatus): string =>
    status === "GRANTED"
      ? t("suivi_consent_granted")
      : status === "REFUSED"
      ? t("suivi_consent_refused")
      : t("suivi_consent_to_collect");

  const consentSourceWord = (source: SuiviConsentSource | null): string =>
    t(`suivi_consent_source_${(source ?? "OTHER").toLowerCase()}`);

  // « Accordé · papier · 12/09 » — the status, then how and when it was collected
  // (nothing collected = the bare « À recueillir »).
  const consentStateWords = (state: SuiviConsentState): string =>
    [
      consentStatusWord(state.status),
      state.status !== null && state.source ? consentSourceWord(state.source) : null,
      state.status !== null && state.consented_at
        ? dayjs(state.consented_at).format("DD/MM")
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  // One chip per option. When the state changed, the superseded one leads so a
  // correction reads as such: « Données enfant : Refusé · appli · 03/09 → Accordé · papier · 12/09 ».
  const consentChipText = (consent: SuiviMemberConsent): string => {
    const current = consentStateWords(consent);
    const previous = consent.previous ? consentStateWords(consent.previous) : null;
    return `${consent.label} : ${previous ? `${previous} → ` : ""}${current}`;
  };

  // Who refused: members of this site by name, the others only as a count — the same
  // visibility discipline as the reference-date rows (the gate is the server's).
  const gateRefusedWho = (gate: SuiviConsentGate): string => {
    const visible = gate.refused_by_user_ids
      .map((userId) => labelForSubject(userId, null))
      .filter(Boolean)
      .join(", ");
    const hidden =
      gate.refused_by_hidden_count > 0
        ? t("suivi_refdate_missing_hidden", { n: gate.refused_by_hidden_count })
        : "";
    return [visible, hidden].filter(Boolean).join(" · ") || t("suivi_member_generic");
  };

  // Opposition shortcut: only on a DOSSIER-scope refusal, and only where a dossier
  // exists to withdraw — the same guard as the « Sortie d'étude du dossier » pill.
  const canOpposeFromGate = (gate: SuiviConsentGate): boolean =>
    gate.state === "REFUSED" &&
    gate.scope === "DOSSIER" &&
    canManage &&
    detail?.grouping_mode === "case" &&
    Boolean(detail?.external_id);

  const openRunner = (row: SuiviFillSequence): void => {
    const next = row.components.find(
      (component) => component.questionnaire_id && !component.is_done
    );
    if (!next) return;
    ionRouter.push(
      `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(rawGroupKey)}/sequences/${row.user_sequence_id}/components/${next.id}`,
      "forward",
      "push"
    );
  };

  const statusWords = (row: SuiviSequenceBase): ReactElement => (
    <StatusText $active={row.status === "IN_PROGRESS"}>
      {row.status === "IN_PROGRESS"
        ? t("suivi_status_in_progress", {
            answered: row.progress.answered,
            total: row.progress.total,
          })
        : t("suivi_status_not_started")}
    </StatusText>
  );

  // A member is contactable with a phone (call) or, failing that, an email.
  const isContactable = (member: SuiviMemberDetail): boolean =>
    member.has_phone || Boolean(member.email);
  const contactLabel = (member: SuiviMemberDetail): string =>
    member.has_phone ? t("suivi_call") : t("suivi_contact");

  const doneCount = (detail?.history ?? []).filter((entry) => entry.kind === "DONE").length;
  const missedCount = (detail?.history ?? []).filter((entry) => entry.kind === "EXPIRED").length;

  const handleMarkReminded = async (): Promise<void> => {
    if (!callMember || !study?.id) return;
    await postSuiviReminderStamp({
      studyId: study.id,
      groupKey: rawGroupKey,
      subjectUserId: callMember.user_id,
    });
    // Await the refetch: the sheet derives its "stamped today" state from the
    // fresh detail, so the button must stay disabled until it lands.
    await fetchDetail();
  };

  const handleRemoveMember = async (): Promise<void> => {
    if (!removeTarget || !study?.id) return;
    setRemoving(true);
    try {
      await deleteSuiviGroupMember({
        studyId: study.id,
        groupKey: rawGroupKey,
        memberUserId: removeTarget.user_id,
      });
      setRemoveTarget(null);
      present(t("suivi_remove_member_done"));
      await fetchDetail();
    } catch (e: any) {
      // 409 = data appeared since the page loaded (deletable is a snapshot).
      present(
        e?.response?.status === 409
          ? t("suivi_remove_member_blocked")
          : t("common_server_error_desc")
      );
      setRemoveTarget(null);
      await fetchDetail();
    } finally {
      setRemoving(false);
    }
  };

  // Exit and death share this tail: close the form, tell the investigator what happened,
  // then re-read. A dossier-wide exit is the one case that must NOT refetch in place —
  // every member is now inactive, so the group key stops resolving and the detail 404s.
  const finishAction = async (message: string, leaveGroup = false): Promise<void> => {
    setActionSaving(false);
    setActionError(null);
    setActionsMember(null);
    setExitTarget(null);
    setExitDossier(false);
    setDeathTarget(null);
    setAccessLinkTarget(null);
    setConsentSheetMember(null);
    setAbsenceTarget(null);
    present(message);
    if (leaveGroup) ionRouter.push(`/${study_slug}/tabs/suivi`, "back", "pop");
    else await fetchDetail();
  };

  const handleExit = async (payload: SuiviExitBody): Promise<void> => {
    if (!study?.id) return;
    setActionSaving(true);
    setActionError(null);
    try {
      if (exitDossier) {
        await postSuiviGroupExit({ studyId: study.id, groupKey: rawGroupKey }, payload);
        await finishAction(t("suivi_exit_dossier_done"), true);
      } else if (exitTarget) {
        await postSuiviMemberExit(
          { studyId: study.id, groupKey: rawGroupKey, memberUserId: exitTarget.user_id },
          payload
        );
        await finishAction(t("suivi_exit_member_done"));
      }
    } catch (e: any) {
      setActionSaving(false);
      setActionError(e?.response?.data?.message ?? t("common_server_error_desc"));
    }
  };

  const handleRecordDeath = async (payload: {
    death_date: string | null;
    note: string | null;
  }): Promise<void> => {
    if (!study?.id || !deathTarget) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviMemberDeath(
        { studyId: study.id, groupKey: rawGroupKey, memberUserId: deathTarget.user_id },
        payload
      );
      await finishAction(t("suivi_death_done"));
    } catch (e: any) {
      setActionSaving(false);
      setActionError(e?.response?.data?.message ?? t("common_server_error_desc"));
    }
  };

  const handleSendAccessLink = async ({ email }: { email: string | null }): Promise<void> => {
    if (!study?.id || !accessLinkTarget) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviMemberAccessLink(
        { studyId: study.id, groupKey: rawGroupKey, memberUserId: accessLinkTarget.user_id },
        { channel: "EMAIL", email, lang: language }
      );
      await finishAction(t("suivi_access_link_done"));
    } catch (e: any) {
      setActionSaving(false);
      // 409 = no contact for the channel, or the researcher configured no invitation
      // from this staff role to the member's role — both are actionable, so show the
      // server's own wording rather than a generic failure.
      setActionError(
        e?.response?.status === 409
          ? t("suivi_access_link_unavailable")
          : e?.response?.data?.message ?? t("common_server_error_desc")
      );
    }
  };

  const handleClearDeath = async (member: SuiviMemberDetail): Promise<void> => {
    if (!study?.id) return;
    try {
      await deleteSuiviMemberDeath({
        studyId: study.id,
        groupKey: rawGroupKey,
        memberUserId: member.user_id,
      });
      await finishAction(t("suivi_death_clear_done"));
    } catch {
      setActionsMember(null);
      present(t("common_server_error_desc"));
      await fetchDetail();
    }
  };

  // The API emits `error_code` (production replaces `message` with the bare status
  // text, so the code is the only stable branch); older builds carried it in message.
  const apiErrorCode = (e: any): string | undefined =>
    e?.response?.data?.error_code ?? e?.response?.data?.message;

  const handleRecordConsents = async (body: SuiviConsentsBody): Promise<void> => {
    if (!study?.id || !consentSheetMember) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviMemberConsents(
        { studyId: study.id, groupKey: rawGroupKey, memberUserId: consentSheetMember.user_id },
        body
      );
      await finishAction(t("suivi_consent_done"));
    } catch (e: any) {
      setActionSaving(false);
      // Someone else (a colleague, or the parent registering in-app) wrote this
      // member's grants meanwhile — the sheet's seed is stale. Refetch in place (the
      // open sheet keeps its choices: its seed is keyed on the member, not the data)
      // so a close-and-reopen starts from what is now recorded (remove-member 409 precedent).
      const conflict = apiErrorCode(e) === "CONSENT_GRANT_CONFLICT";
      setActionError(
        conflict
          ? t("suivi_consent_conflict")
          : e?.response?.data?.message ?? t("common_server_error_desc")
      );
      if (conflict) await fetchDetail();
    }
  };

  const handleDeclareAbsence = async (): Promise<void> => {
    if (!study?.id || !absenceTarget) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviRoleAbsence({
        studyId: study.id,
        groupKey: rawGroupKey,
        roleId: absenceTarget.study_role_id,
      });
      await finishAction(t("suivi_absence_declared_done"));
    } catch (e: any) {
      setActionSaving(false);
      // A real member of that role is already waited on (registered, or holding a
      // grant) — declaring the role absent would contradict the dossier.
      setActionError(
        apiErrorCode(e) === "ROLE_HAS_MEMBER"
          ? t("suivi_absence_has_member", {
              role: absenceTarget.role_label ?? t("suivi_member_generic"),
            })
          : e?.response?.data?.message ?? t("common_server_error_desc")
      );
    }
  };

  // Undo needs no confirm: it only restores the wait (clear-death precedent).
  const handleClearAbsence = async (absence: SuiviRoleAbsence): Promise<void> => {
    if (!study?.id) return;
    try {
      await deleteSuiviRoleAbsence({
        studyId: study.id,
        groupKey: rawGroupKey,
        roleId: absence.study_role_id,
      });
      await finishAction(t("suivi_absence_cleared_done"));
    } catch {
      present(t("common_server_error_desc"));
      await fetchDetail();
    }
  };

  // « 2 terminés · 1 manqués » — one string, so the fold row reads as a sentence.
  const historySummary = [
    doneCount > 0 ? t("suivi_history_done", { count: doneCount }) : null,
    missedCount > 0 ? t("suivi_history_missed", { count: missedCount }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ResonanceLayout>
      {/* Single fragment child — ResonanceLayout's children prop is one element. */}
      <>
      <Page $wide>
        {isError ? (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : detail === null ? (
          <ResonanceLoader />
        ) : (
          <Columns>
            {/* The dossier: who is in it, its dates, its consents. */}
            <Column>
              <SectionHeader title={t("suivi_members")} icon={peopleOutline} />
              <ListCard>
                {detail.members.map((member) => {
                  const name = [member.firstname, member.lastname].filter(Boolean).join(" ");
                  const hasTags = Boolean(
                    member.exited ||
                      member.deceased ||
                      member.invited_at ||
                      member.last_reminder_stamp ||
                      missingReferenceDates(member).length > 0 ||
                      consentToCollect(member)
                  );
                  return (
                    <MemberRow key={member.user_id}>
                      {/* Someone who left or died reads as such at a glance. */}
                      <MemberInitial $muted={member.exited || member.deceased} aria-hidden="true">
                        {(member.role_label ?? "?").charAt(0).toUpperCase()}
                      </MemberInitial>
                      {/* Ordinal role label leads; the name is garnish once collected. */}
                      <MemberIdentity>
                        <RowTitle>{memberRoleLabel(member)}</RowTitle>
                        {name && <MemberName>{name}</MemberName>}
                      </MemberIdentity>
                      <MemberActions>
                        {/* Someone who left the study is not someone to call. */}
                        {isContactable(member) && !member.exited && (
                          <OutlinePill type="button" onClick={() => setCallMember(member)}>
                            <IonIcon
                              icon={member.has_phone ? callOutline : mailOutline}
                              aria-hidden="true"
                            />
                            {contactLabel(member)}
                          </OutlinePill>
                        )}
                        {canManage && (
                          <IconButton
                            type="button"
                            aria-label={t("suivi_member_actions")}
                            onClick={() => setActionsMember(member)}
                          >
                            <IonIcon icon={ellipsisHorizontal} aria-hidden="true" />
                          </IconButton>
                        )}
                      </MemberActions>
                      {/* Status first: an exited or deceased member must read as such
                          before anything else on the row. */}
                      {hasTags && (
                        <MemberTags>
                          {member.deceased && <QuietTag>{t("suivi_deceased_tag")}</QuietTag>}
                          {/* Only while it still matters: once they have registered, when
                              the link was sent is no longer the useful fact. */}
                          {member.invited_at && !member.registered && !member.exited && (
                            <QuietTag>
                              <IonIcon icon={mailOutline} aria-hidden="true" />
                              {t("suivi_invited_on", {
                                date: dayjs(member.invited_at).format("DD/MM"),
                              })}
                            </QuietTag>
                          )}
                          {member.exited && (
                            <QuietTag>
                              <IonIcon icon={exitOutline} aria-hidden="true" />
                              {member.exit_reason_code
                                ? t("suivi_exited_tag_reason", {
                                    reason: t(exitReasonLabelKey(member.exit_reason_code)),
                                  })
                                : t("suivi_exited_tag")}
                            </QuietTag>
                          )}
                          {/* A fresh placeholder has none of the tags above — this one is
                              what tells the investigator the fiche is still to be keyed in. */}
                          {consentToCollect(member) && (
                            <QuietTag $tone="info">
                              <IonIcon icon={documentTextOutline} aria-hidden="true" />
                              {t("suivi_consent_to_collect_tag")}
                            </QuietTag>
                          )}
                          {member.last_reminder_stamp && (
                            <QuietTag>
                              <IonIcon icon={notificationsOutline} aria-hidden="true" />
                              {t("suivi_reminded_on", {
                                date: dayjs(member.last_reminder_stamp.at).format("DD/MM"),
                              })}
                            </QuietTag>
                          )}
                          {/* A dossier rule is waiting on this member's own date — the
                              hint names the rule, the card below says who else is missing. */}
                          {missingReferenceDates(member).map((refDate) => (
                            <QuietTag key={refDate.definition_id} $tone="info">
                              <IonIcon icon={calendarOutline} aria-hidden="true" />
                              {t("suivi_refdate_member_missing", { label: refDate.label })}
                            </QuietTag>
                          ))}
                        </MemberTags>
                      )}
                    </MemberRow>
                  );
                })}

                {canManage && (
                  <CardFooter>
                    <CardAction type="button" onClick={() => setAddOpen(true)}>
                      <IonIcon icon={personAddOutline} aria-hidden="true" />
                      {t("suivi_add_member_button")}
                    </CardAction>
                    {/* Dossier-wide, and only where a dossier exists: in subject grouping
                        mode there is nothing but the single member to withdraw. */}
                    {detail.grouping_mode === "case" && detail.external_id && (
                      <CardAction
                        type="button"
                        $muted
                        onClick={() => {
                          setExitDefaultReason(ExitReasonId.WITHDRAWAL);
                          setExitDossier(true);
                          setActionError(null);
                        }}
                      >
                        <IonIcon icon={exitOutline} aria-hidden="true" />
                        {t("suivi_exit_dossier_button")}
                      </CardAction>
                    )}
                  </CardFooter>
                )}
              </ListCard>

              {/* Dossier rules giving date-less members (parents) a derived anchor date.
                  Case mode only (caseReferenceDates is [] otherwise). One quiet line per
                  rule — never a banner: the dossier must still read whole. */}
              {caseReferenceDates.length > 0 && (
                <>
                  <SectionHeader title={t("suivi_refdates")} icon={calendarOutline} />
                  <ListCard>
                    {caseReferenceDates.map((refDate) => {
                      // Unknown status from a newer API → the neutral "to complete" mark.
                      const mark = REFDATE_MARK[refDate.status] ?? REFDATE_MARK.UNCONFIGURED;
                      return (
                        <Row key={refDate.definition_id}>
                          <StatusMark $tone={mark.tone} aria-hidden="true">
                            <IonIcon icon={mark.icon} />
                          </StatusMark>
                          <RowMain>
                            <RowTitle>{refDate.label}</RowTitle>
                            {refDate.status === "RESOLVED" && refDate.derived_value && (
                              <RowSub>
                                <span>
                                  {t("suivi_refdate_resolved", {
                                    date: dayjs(refDate.derived_value).format("DD/MM/YYYY"),
                                  })}
                                  {/* Whose own date was retained — named only when that
                                      member is within this investigator's site. */}
                                  {refDate.derived_from_user_id != null &&
                                    labelForSubject(refDate.derived_from_user_id, null) &&
                                    ` (${labelForSubject(refDate.derived_from_user_id, null)})`}
                                </span>
                                {/* Why the dossier resolved on fewer members than declared:
                                    deceased members leave the gate (« 1 sur 1 (1 décès) »). */}
                                {refDate.deceased_source_count > 0 && (
                                  <QuietTag>
                                    {t("suivi_refdate_waiting", {
                                      done: refDate.resolved_source_count,
                                      total: refDate.expected_source_count,
                                    })}
                                    {` ${t("suivi_refdate_deceased", {
                                      n: refDate.deceased_source_count,
                                    })}`}
                                  </QuietTag>
                                )}
                              </RowSub>
                            )}
                            {refDate.status === "WAITING" && (
                              <>
                                <RowSub>
                                  <QuietTag $tone="info">
                                    {t("suivi_refdate_waiting", {
                                      done: refDate.resolved_source_count,
                                      total: refDate.expected_source_count,
                                    })}
                                    {/* Why the denominator shrank: deceased members leave the gate. */}
                                    {refDate.deceased_source_count > 0 &&
                                      ` ${t("suivi_refdate_deceased", {
                                        n: refDate.deceased_source_count,
                                      })}`}
                                  </QuietTag>
                                </RowSub>
                                {missingReferenceDateWho(refDate) && (
                                  <RowSub>
                                    <span>
                                      {t("suivi_refdate_missing", {
                                        who: missingReferenceDateWho(refDate),
                                      })}
                                    </span>
                                  </RowSub>
                                )}
                              </>
                            )}
                            {refDate.status === "NO_SOURCE" && (
                              <>
                                <RowSub>
                                  <QuietTag>
                                    {refDate.deceased_source_count > 0
                                      ? t("suivi_refdate_no_source_death")
                                      : t("suivi_refdate_no_source")}
                                  </QuietTag>
                                </RowSub>
                                {/* Not a dead end when a rule does not wait for all: naming who
                                    owes a date says what unblocks the recipients. */}
                                {missingReferenceDateWho(refDate) && (
                                  <RowSub>
                                    <span>
                                      {t("suivi_refdate_missing", {
                                        who: missingReferenceDateWho(refDate),
                                      })}
                                    </span>
                                  </RowSub>
                                )}
                              </>
                            )}
                            {refDate.status === "UNCONFIGURED" && (
                              <RowSub>
                                <QuietTag>{t("suivi_refdate_unconfigured")}</QuietTag>
                              </RowSub>
                            )}
                          </RowMain>
                        </Row>
                      );
                    })}
                  </ListCard>
                </>
              )}

              {/* « Consentements » — per-member option status, dossier absence declarations
                  and one rollup per consent-gated role. Same quiet structure as the
                  reference-date card: words in tags, never a banner. Recording goes through
                  the member ⋯ menu only (one affordance per member row). */}
              {hasConsentSection && (
                <>
                  <SectionHeader title={t("suivi_consents")} icon={shieldCheckmarkOutline} />
                  <ListCard>
                    {detail.members
                      .filter((member) => (member.consents?.length ?? 0) > 0)
                      .map((member) => (
                        <Row key={`consent-${member.user_id}`}>
                          <RowMain>
                            <RowTitle>{memberDisplayName(member)}</RowTitle>
                            <RowSub>
                              {(member.consents ?? []).map((consent) => (
                                <QuietTag
                                  key={consent.consent_option_id}
                                  $tone={consentTone(consent.status)}
                                >
                                  <IonIcon icon={consentIcon(consent.status)} aria-hidden="true" />
                                  {consentChipText(consent)}
                                </QuietTag>
                              ))}
                            </RowSub>
                          </RowMain>
                        </Row>
                      ))}

                    {/* Dossier-level absences: « Aucun Père — déclaré » with its undo, or the
                        declare affordance while the role is declarable and not yet declared.
                        The server still refuses when a real member is waited on (409). */}
                    {(detail.role_absences ?? []).map((absence) => {
                      const role = absence.role_label ?? t("suivi_member_generic");
                      if (absence.is_absent) {
                        return (
                          <Row key={`absence-${absence.study_role_id}`}>
                            <RowMain>
                              <RowTitle>{role}</RowTitle>
                              <RowSub>
                                <span>
                                  {t("suivi_absence_declared_line", {
                                    role,
                                    source: consentSourceWord(absence.source),
                                    date: absence.declared_at
                                      ? dayjs(absence.declared_at).format("DD/MM/YYYY")
                                      : "—",
                                  })}
                                </span>
                              </RowSub>
                            </RowMain>
                            {canManage && (
                              <OutlinePill
                                type="button"
                                onClick={() => void handleClearAbsence(absence)}
                              >
                                {t("suivi_absence_clear")}
                              </OutlinePill>
                            )}
                          </Row>
                        );
                      }
                      if (!canDeclareAbsent(absence)) return null;
                      return (
                        <Row key={`absence-${absence.study_role_id}`}>
                          <RowMain>
                            <RowTitle>{t("suivi_absence_prompt", { role })}</RowTitle>
                          </RowMain>
                          <OutlinePill
                            type="button"
                            onClick={() => {
                              setActionError(null);
                              setAbsenceTarget(absence);
                            }}
                          >
                            {t("suivi_absence_declare_short")}
                          </OutlinePill>
                        </Row>
                      );
                    })}

                    {/* One rollup per consent-gated role: what the questionnaires ABOUT that
                        role are waiting on. NOT_APPLICABLE (no dossier number) says nothing
                        useful here and is skipped. */}
                    {(detail.consent_gates ?? [])
                      .filter((gate) => gate.state !== "NOT_APPLICABLE")
                      .map((gate) => (
                        <Row key={`gate-${gate.study_role_id}`}>
                          <RowMain>
                            <RowTitle>
                              {t("suivi_consent_gate_title", {
                                role: gate.role_label ?? t("suivi_member_generic"),
                              })}
                            </RowTitle>
                            <RowSub>
                              {gate.state === "WAITING" && (
                                <QuietTag $tone="info">
                                  <IonIcon icon={hourglassOutline} aria-hidden="true" />
                                  {t("suivi_consent_gate_waiting", {
                                    // No grantor role at all = unsatisfiable in-app: name
                                    // the option so the team sees what to link.
                                    roles: gate.missing_role_labels.length
                                      ? gate.missing_role_labels.join(", ")
                                      : gate.option_label,
                                  })}
                                </QuietTag>
                              )}
                              {gate.state === "REFUSED" && (
                                <QuietTag $tone="danger">
                                  <IonIcon icon={banOutline} aria-hidden="true" />
                                  {t("suivi_consent_gate_blocked", { who: gateRefusedWho(gate) })}
                                </QuietTag>
                              )}
                              {gate.state === "SATISFIED" && (
                                <QuietTag $tone="success">
                                  <IonIcon icon={checkmark} aria-hidden="true" />
                                  {t("suivi_consent_gate_satisfied")}
                                </QuietTag>
                              )}
                            </RowSub>
                          </RowMain>
                          {/* A refusal blocks the rows; leaving the study is a separate
                              decision — offered here, preselecting the OPPOSITION reason. */}
                          {canOpposeFromGate(gate) && (
                            <OutlinePill
                              type="button"
                              onClick={() => {
                                setExitDefaultReason(ExitReasonId.OPPOSITION);
                                setExitDossier(true);
                                setActionError(null);
                              }}
                            >
                              {t("suivi_consent_opposition_exit")}
                            </OutlinePill>
                          )}
                        </Row>
                      ))}
                  </ListCard>
                </>
              )}
            </Column>

            {/* The work: what I fill, what the family still owes, what is done. */}
            <Column>
              {detail.to_fill.length > 0 && (
                <>
                  <SectionHeader
                    title={t("suivi_to_fill")}
                    icon={createOutline}
                    count={detail.to_fill.length}
                  />
                  <ListCard>
                    {detail.to_fill.map((row) => (
                      <Row key={row.user_sequence_id}>
                        <RowMain>
                          <RowTitle>{row.label ?? t("suivi_questionnaire")}</RowTitle>
                          <RowSub>
                            {labelForSubject(row.subject_user_id, row.subject_role_label) && (
                              <span>
                                {labelForSubject(row.subject_user_id, row.subject_role_label)}
                              </span>
                            )}
                            <DueChip endDate={row.end_date} t={t} />
                          </RowSub>
                        </RowMain>
                        {row.filled_by_colleague ? (
                          <RowEnd>
                            <QuietTag $tone="success">
                              <IonIcon icon={checkmark} aria-hidden="true" />
                              {t("suivi_filled_by_colleague")}
                            </QuietTag>
                          </RowEnd>
                        ) : (
                          <FillPill type="button" onClick={() => openRunner(row)}>
                            <IonIcon
                              icon={row.progress.answered > 0 ? playOutline : createOutline}
                              aria-hidden="true"
                            />
                            {row.progress.answered > 0
                              ? t("suivi_continue_progress", {
                                  answered: row.progress.answered,
                                  total: row.progress.total,
                                })
                              : t("suivi_fill")}
                          </FillPill>
                        )}
                      </Row>
                    ))}
                  </ListCard>
                </>
              )}

              {detail.awaiting_subjects.length > 0 && (
                <>
                  <SectionHeader
                    title={t("suivi_parents_side")}
                    icon={hourglassOutline}
                    count={detail.awaiting_subjects.length}
                  />
                  <ListCard>
                    {detail.awaiting_subjects.map((row) => {
                      const member = memberById.get(row.subject_user_id);
                      return (
                        <Row key={row.user_sequence_id}>
                          <RowMain>
                            <RowTitle>{row.label ?? t("suivi_questionnaire")}</RowTitle>
                            <RowSub>
                              {labelForSubject(row.subject_user_id, row.subject_role_label) && (
                                <span>
                                  {labelForSubject(row.subject_user_id, row.subject_role_label)}
                                </span>
                              )}
                              {statusWords(row)}
                              {/* Per-child questionnaire: the PARENT must answer it,
                                  not the child the row is about. */}
                              {row.answerer_user_id != null &&
                                labelForSubject(row.answerer_user_id, null) && (
                                  <QuietTag>
                                    {t("suivi_to_answer_by", {
                                      who: labelForSubject(row.answerer_user_id, null),
                                    })}
                                  </QuietTag>
                                )}
                              {member?.last_reminder_stamp && (
                                <QuietTag>
                                  <IonIcon icon={notificationsOutline} aria-hidden="true" />
                                  {t("suivi_reminded_on", {
                                    date: dayjs(member.last_reminder_stamp.at).format(
                                      "DD/MM"
                                    ),
                                  })}
                                </QuietTag>
                              )}
                            </RowSub>
                          </RowMain>
                          <RowEnd>
                            <DueChip endDate={row.end_date} t={t} />
                            {member && isContactable(member) && (
                              <OutlinePill
                                type="button"
                                onClick={() => setCallMember(member)}
                              >
                                <IonIcon
                                  icon={member.has_phone ? callOutline : mailOutline}
                                  aria-hidden="true"
                                />
                                {contactLabel(member)}
                              </OutlinePill>
                            )}
                          </RowEnd>
                        </Row>
                      );
                    })}
                  </ListCard>
                </>
              )}

              {detail.to_fill.length === 0 && detail.awaiting_subjects.length === 0 && (
                <EmptyState icon={checkmarkDoneOutline} tone="success" card>
                  {t("suivi_empty_group")}
                </EmptyState>
              )}

              {detail.history.length > 0 && (
                <ListCard>
                  <Row
                    as="button"
                    type="button"
                    $tappable
                    aria-expanded={historyOpen}
                    onClick={() => setHistoryOpen((open) => !open)}
                  >
                    <IconTile $tone="neutral" aria-hidden="true">
                      <IonIcon icon={timeOutline} />
                    </IconTile>
                    <RowMain>
                      <RowTitle>{t("suivi_history")}</RowTitle>
                      {historySummary && <RowSub as="span">{historySummary}</RowSub>}
                    </RowMain>
                    <FoldGlyph $open={historyOpen} icon={chevronDown} aria-hidden="true" />
                  </Row>
                  {historyOpen &&
                    detail.history.map((entry) => (
                      <Row key={entry.user_sequence_id}>
                        {/* Done = green check, missed = grey dash — never actionable. */}
                        <StatusMark
                          $tone={entry.kind === "DONE" ? "success" : "neutral"}
                          aria-hidden="true"
                        >
                          <IonIcon icon={entry.kind === "DONE" ? checkmark : remove} />
                        </StatusMark>
                        <RowMain>
                          <RowTitle style={{ fontWeight: 500 }}>
                            {entry.label ?? t("suivi_questionnaire")}
                          </RowTitle>
                          <RowSub>
                            {labelForSubject(entry.subject_user_id, entry.subject_role_label) && (
                              <>
                                <span>
                                  {labelForSubject(entry.subject_user_id, entry.subject_role_label)}
                                </span>
                                <MetaSep />
                              </>
                            )}
                            <span>
                              {entry.kind === "DONE"
                                ? t("suivi_done_on", {
                                    date: dayjs(entry.at).format("DD/MM/YYYY"),
                                  })
                                : t("suivi_missed_on", {
                                    date: dayjs(entry.at).format("DD/MM/YYYY"),
                                  })}
                            </span>
                            {/* Who produced this answer, so a per-child questionnaire does
                                not read as the child's own. A parent → their name; the
                                investigator viewing (ME) → « vous ». */}
                            {entry.answered_by === "ME" && (
                              <QuietTag>
                                {t("suivi_answered_by", { who: t("suivi_answered_by_you") })}
                              </QuietTag>
                            )}
                            {entry.answered_by === "FAMILY" &&
                              entry.answered_by_user_id != null &&
                              labelForSubject(entry.answered_by_user_id, null) && (
                                <QuietTag>
                                  {t("suivi_answered_by", {
                                    who: labelForSubject(entry.answered_by_user_id, null),
                                  })}
                                </QuietTag>
                              )}
                          </RowSub>
                        </RowMain>
                      </Row>
                    ))}
                </ListCard>
              )}

              {/* Future windows are sentences, never cards (design contract):
                  the investigator's next fill first, then each member's own. */}
              {detail.next_windows.length > 0 && (
                <FooterStack>
                  {detail.next_windows.map((window) => (
                    <FooterLine
                      key={`${window.kind}-${window.subject_user_id}-${window.sequence_id}`}
                    >
                      <IonIcon icon={calendarClearOutline} aria-hidden="true" />
                      <span>
                        {window.kind === "FILL"
                          ? t("suivi_next_window_fill", {
                              label: window.label ?? t("suivi_questionnaire"),
                              date: dayjs(window.opens_at).format("DD/MM/YYYY"),
                            })
                          : t("suivi_next_window_subject", {
                              role:
                                labelForSubject(window.subject_user_id, window.subject_role_label)
                                ?? t("suivi_member_generic"),
                              label: window.label ?? t("suivi_questionnaire"),
                              date: dayjs(window.opens_at).format("DD/MM/YYYY"),
                            })}
                      </span>
                    </FooterLine>
                  ))}
                </FooterStack>
              )}
            </Column>
          </Columns>
        )}
      </Page>

      <CallSheet
        // LIVE member from the freshest detail (not the state snapshot) so the
        // sheet reflects the reminder stamp the moment the refetch lands.
        member={
          callMember
            ? memberById.get(callMember.user_id) ?? callMember
            : null
        }
        roleLabel={callMember ? memberRoleLabel(callMember) : undefined}
        pending={(detail?.awaiting_subjects ?? []).filter(
          (row) => row.subject_user_id === callMember?.user_id
        )}
        onClose={() => setCallMember(null)}
        onMarkReminded={handleMarkReminded}
      />

      <AddMemberModal
        isOpen={addOpen}
        groupKey={rawGroupKey}
        onDismiss={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          present(t("suivi_add_member_done"));
          fetchDetail();
        }}
      />

      {/* Remove confirm — only reachable through a `deletable` member row. */}
      <IonModal
        isOpen={removeTarget !== null}
        className="resonance-modal"
        backdropDismiss
        onWillDismiss={() => setRemoveTarget(null)}
      >
        <ModalCard>
          <ModalTitle>{t("suivi_remove_member_title")}</ModalTitle>
          <ModalDescription>
            {t("suivi_remove_member_text", {
              // Ordinal label (+ name if one appeared): a destructive confirm must
              // say WHICH « Enfant » it is about, exactly like the row that was tapped.
              role: removeTarget
                ? memberDisplayName(removeTarget)
                : t("suivi_member_generic"),
            })}
          </ModalDescription>
          <ModalActions>
            <ModalQuietButton onClick={() => setRemoveTarget(null)}>
              {t("common_cancel")}
            </ModalQuietButton>
            <ModalPrimaryButton onClick={handleRemoveMember} disabled={removing}>
              {t("suivi_remove_member_confirm")}
            </ModalPrimaryButton>
          </ModalActions>
        </ModalCard>
      </IonModal>

      <MemberActionsModal
        isOpen={actionsMember !== null}
        member={actionsMember}
        memberName={actionsMember ? memberDisplayName(actionsMember) : ""}
        onDismiss={() => setActionsMember(null)}
        onRemove={() => {
          const target = actionsMember;
          setActionsMember(null);
          setRemoveTarget(target);
        }}
        onExit={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setExitDefaultReason(ExitReasonId.WITHDRAWAL);
          setExitTarget(target);
        }}
        onRecordDeath={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setDeathTarget(target);
        }}
        onClearDeath={() => {
          if (actionsMember) void handleClearDeath(actionsMember);
        }}
        onSendAccessLink={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setAccessLinkTarget(target);
        }}
        onRecordConsents={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setConsentSheetMember(target);
        }}
      />

      <AccessLinkModal
        isOpen={accessLinkTarget !== null}
        memberName={accessLinkTarget ? memberDisplayName(accessLinkTarget) : ""}
        knownEmail={accessLinkTarget?.email ?? null}
        alreadySentAt={accessLinkTarget?.invited_at ?? null}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setAccessLinkTarget(null);
          setActionError(null);
        }}
        onConfirm={handleSendAccessLink}
      />

      <ExitModal
        isOpen={exitTarget !== null || exitDossier}
        isDossier={exitDossier}
        targetName={
          exitDossier
            ? detail?.external_id ?? ""
            : exitTarget
            ? memberDisplayName(exitTarget)
            : ""
        }
        saving={actionSaving}
        error={actionError}
        defaultReasonId={exitDefaultReason}
        onDismiss={() => {
          setExitTarget(null);
          setExitDossier(false);
          setActionError(null);
        }}
        onConfirm={handleExit}
      />

      <DeathModal
        isOpen={deathTarget !== null}
        targetName={deathTarget ? memberDisplayName(deathTarget) : ""}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setDeathTarget(null);
          setActionError(null);
        }}
        onConfirm={handleRecordDeath}
      />

      <ConsentSheet
        isOpen={consentSheetMember !== null}
        // LIVE member (freshest detail), like the CallSheet: the pills seed from what
        // is recorded now, not from the snapshot taken when the menu opened.
        member={
          consentSheetMember
            ? memberById.get(consentSheetMember.user_id) ?? consentSheetMember
            : null
        }
        memberName={consentSheetMember ? memberDisplayName(consentSheetMember) : ""}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setConsentSheetMember(null);
          setActionError(null);
        }}
        onConfirm={handleRecordConsents}
      />

      <AbsenceConfirmModal
        isOpen={absenceTarget !== null}
        roleLabel={absenceTarget?.role_label ?? t("suivi_member_generic")}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setAbsenceTarget(null);
          setActionError(null);
        }}
        onConfirm={handleDeclareAbsence}
      />
      </>
    </ResonanceLayout>
  );
};

// ref_exit_reasons code → translation key. Unknown codes (a reason added server-side
// after this build) fall back to « Autre » rather than rendering a raw code.
const exitReasonLabelKey = (code: string): string =>
  ({
    WITHDRAWAL: "suivi_exit_reason_withdrawal",
    SCREEN_FAILURE: "suivi_exit_reason_screen_failure",
    DEATH: "suivi_deceased_tag",
    LOST_TO_FOLLOW_UP: "suivi_exit_reason_lost",
    ENROLLMENT_ERROR: "suivi_exit_reason_error",
    OPPOSITION: "suivi_exit_reason_opposition",
  }[code] ?? "suivi_exit_reason_other");

/* Member row: avatar · identity · actions on the first line; the status tags run
   underneath across the identity AND actions columns, so a narrow phone row never
   squeezes them into the space left beside the « Appeler » pill. The tag line is
   an implicit grid row — rows without tags carry no empty gap. */
const MemberRow = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 10px;
  padding: 14px 16px;

  ${insetSeparator}
`;

const MemberIdentity = styled.div`
  min-width: 0;
`;

const MemberName = styled.p`
  margin: 2px 0 0;
  font-size: 13.5px;
  line-height: 1.3;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.62);
  overflow-wrap: anywhere;
`;

const MemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MemberTags = styled.div`
  grid-column: 2 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export default GroupSpace;
